import { useNavigate, useParams, useLocation } from "react-router-dom";
import { CompetitionWithStats } from "../../../types/competition.types";
import { useCallback, useEffect, useState } from "react";
import { TeamWithStats } from "../../../types/team.types";
import { CompetitionsAPI } from "../../../APIs/competitions.api";
import { CompetitionStats } from "./competition-stats";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { TeamsAPI } from "../../../APIs/teams.api";

export function CompetitionView() {
  const { id } = useParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [competition, setCompetition] = useState<CompetitionWithStats | null>(
    null
  );
  const [teams, setTeams] = useState<TeamWithStats[]>([]);
  const [showAddTeamButton, setShowAddTeamButton] = useState(false);

  const getInitialTab = useCallback(() => {
    const hash = location.hash.replace("#", "").toLowerCase();
    return ["información", "equipos"].includes(hash) ? hash : "información";
  }, [location.hash]);

  const [activeTab, setActiveTab] = useState(getInitialTab());

  const handleTabChange = (tab: string) => {
    const normalizedTab = tab.toLowerCase();
    setActiveTab(normalizedTab);
    navigate(`${location.pathname}#${normalizedTab}`, { replace: true });
  };

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(getInitialTab());
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [getInitialTab]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const numberId = Number(id);
        if (!id || isNaN(numberId)) {
          navigate("/dashboard/competitions", { replace: true });
          return;
        }
        const compResponse = await CompetitionsAPI.getCompetition(numberId);
        if (compResponse.statusCode !== 200) {
          navigate("/dashboard/competitions", { replace: true });
          return;
        }
        const competitionData = await compResponse.data;
        setCompetition(competitionData);

        const currentDateUTC = new Date().toISOString();
        if (
          competitionData.registration_start < currentDateUTC &&
          competitionData.registration_end > currentDateUTC
        ) {
          setShowAddTeamButton(true);
        }
        const teamsResponse = await TeamsAPI.getTeam(competitionData.id);
        if (teamsResponse.statusCode === 200) {
          setTeams(teamsResponse.data);
        }
      } catch (err) {
        navigate("/dashboard/competitions", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  if (!competition || !teams || isLoading) return null;

  return (
    <div className="">
      <div className="border-b border-gray-200">
        <div className="flex items-center justify-between pr-2">
          <nav className="flex space-x-10" aria-label="Tabs">
            {["Información", "Equipos"].map((tab) => {
              const normalizedTab = tab.toLowerCase();
              const isActive = activeTab === normalizedTab;
              return (
                <button
                  key={tab}
                  className={`px-4 py-4 text-sm font-medium relative transition-colors duration-200
                    ${
                      isActive
                        ? "text-blue-600"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  onClick={() => handleTabChange(normalizedTab)}
                >
                  {tab}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t transition-all duration-300" />
                  )}
                </button>
              );
            })}
          </nav>
          <button
            onClick={() => navigate("/dashboard/competitions")}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors py-3"
          >
            <ArrowLeftIcon className="h-6 w-6" />
            <span className="text-sm font-medium">Volver a competencias</span>
          </button>
        </div>
      </div>
      {activeTab === "información" ? (
        <CompetitionStats competition={competition} />
      ) : // <CompetitionTeams teams={teams} showAddTeamButton={showAddTeamButton} />
      null}
    </div>
  );
}
